import { Component, Input, OnInit, EventEmitter, Output, HostListener } from '@angular/core';
import { InputType } from 'src/app/enums';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import {
  END_USER_TYPE_OPTIONS,
  MESSAGE_CONSTANTS,
  NIGHTWEB_APP_CONSTANT,
  STATUS_CODES,
  US_STATES,
  ENDUSER,
} from 'src/app/constants';
import {
  AdminService,
  OrganizationService,
  ToasterService,
  UserService,
} from 'src/app/services';
import { DateUtility, NightwebUtil } from 'src/app/utils';
import { Router } from '@angular/router';

@Component({
    selector: 'app-add-edit-enduser-scrollable-form',
    templateUrl: './add-edit-enduser-scrollable-form.component.html',
    styleUrls: ['./add-edit-enduser-scrollable-form.component.css'],
    standalone: false
})
export class AddEditEnduserScrollableFormComponent implements OnInit {
  @Output() dataEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() formData!: any;

  inputType = InputType;
  selectedNavIndex: number = 0;

  enduserSideNav = [
    { label: 'Basic Information', value: 'basicInformation' },
    {
      label: 'Personal Safety Information',
      value: 'personalSafetyInformation',
    },
    { label: 'Person Causing Harm', value: 'personCausingHarm' },
    { label: 'Household Information', value: 'householdInformation' },
  ];

  userForm!: FormGroup;
  loading = false;
  submitted = false;
  organizationsList: any[] = [];
  advocateList: any[] = [];
  phoneMask = NIGHTWEB_APP_CONSTANT.phoneMask;
  status_codes = STATUS_CODES;
  states = US_STATES;
  typeOfResidences = ENDUSER.TYPE_OF_RESIDENCE_LIST;
  entranceLocations = ENDUSER.ENTRANCE_LOCATION_LIST;
  homeTypes = ENDUSER.HOME_TYPE_LIST;
  languageList = ENDUSER.LANGUAGE_LIST;
  mdbDateFormat = NIGHTWEB_APP_CONSTANT.mdbDefaultDateFormat;
  maxDate: Date = new Date();
  role: string = '';
  genderList: any[] = NIGHTWEB_APP_CONSTANT.genderList;
  userTypeList = END_USER_TYPE_OPTIONS;
  /** Survivor/Perpetrator — only when selected org has threat-pair enabled */
  showUserType = false;
  editEnduser = false;
  defaultMomentDateFormat = NIGHTWEB_APP_CONSTANT.defaultMomentDateFormat;
  FORM_CONTROL = ENDUSER.FORM_CONTROL;
  isLoggedInEndUser: boolean = this.nightwebUtil.isEndUser();
  saveButtonText: string = this.isLoggedInEndUser ? 'Save Profile' : 'Save';

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private dateUtility: DateUtility,
    private adminService: AdminService,
    private nightwebUtil: NightwebUtil,
    private toasterService: ToasterService,
    private organizationService: OrganizationService,
    private router: Router
  ) {
    this.role = this.nightwebUtil.getRole();
    this.setUserForm();
  }

  get demographic(): FormGroup {
    return this.userForm.get('demographic') as FormGroup;
  }

  get address(): FormGroup {
    return this.userForm.controls['demographic']?.get('address') as FormGroup;
  }

  get home(): FormGroup {
    return this.address?.get('home') as FormGroup;
  }

  get info(): FormGroup {
    return this.userForm?.get('info') as FormGroup;
  }

  get householdMember(): FormGroup {
    return this.userForm.controls['info']?.get('householdMember') as FormGroup;
    //return this.userForm?.get('householdMember') as FormGroup;
  }

  get typeOfResidence() {
    return this.userForm.get('info.typeOfResidence')?.value;
  }

  ngOnInit(): void {
    if (this.isLoggedInEndUser) {
      this.userForm.controls['advocateId'].disable();
      this.userForm.controls['organizationId'].disable();
      this.userForm.controls['accountNumber'].disable();
      this.userForm.controls['userType'].disable();
    }
    const devicePhone = this.formData?.activeDevice?.info?.associatedPhone
      ? this.formData?.activeDevice?.info?.associatedPhone
      : '';
    if (navigator.userAgent.includes('Edg')) {
      document.documentElement.classList.add('edge-browser');
    }
    this.getAllOrganizations();
    if (Object.keys(this.formData).length > 0) {
      this.editEnduser = true;
      const { home, work } = this.formData?.demographic?.address ?? {};
      //const { householdMember } = this.formData?.info;
      this.userForm.patchValue({
        ...this.formData,
        devicePhone: devicePhone,
      });
      if (this.formData?.organizationId) {
        this.getAdvocates(this.formData.organizationId);
      }
      this.userForm?.get('demographic')?.patchValue({
        address: home,
        dob: this.formData?.demographic?.dob
          ? this.dateUtility.formatDate(
              this.userForm.get('demographic')?.value?.dob,
              NIGHTWEB_APP_CONSTANT.defaultMomentDateFormat
            )
          : '',
      });

      this.userForm?.get('demographic')?.patchValue({
        language: this.formData?.demographic?.language || 'ENG',
      });

      this.userForm?.get('info')?.patchValue({
        householdMember: this.formData?.info?.householdMember
          ? this.formData?.info?.householdMember
          : '',
        perpetratorDob: this.formData?.info?.perpetratorDob
          ? this.dateUtility.formatDate(
              this.userForm.get('info')?.value?.perpetratorDob,
              NIGHTWEB_APP_CONSTANT.defaultMomentDateFormat
            )
          : '',
        entranceLocation: this.formData?.info?.entranceLocation
          ? this.nightwebUtil.convertToArray(
              this.formData?.info?.entranceLocation
            )
          : [],
      });
    }
    this.checkTypeOfResidence();
  }

  setUserForm() {
    this.userForm = this.formBuilder.group({
      id: [],
      demographic: this.formBuilder.group({
        firstName: ['', Validators.required],
        middleName: [''],
        lastName: ['', Validators.required],
        dob: ['', Validators.required],
        gender: ['Male'],
        phone: [''],
        email: [''],
        language: ['ENG'],
        address: this.formBuilder.group({
          home: this.formBuilder.group({
            addressLine1: ['', Validators.required],
            addressLine2: [''],
            city: ['', Validators.required],
            state: ['', Validators.required],
            zip: ['', [Validators.pattern(NIGHTWEB_APP_CONSTANT.zipPattern)]],
          }),
        }),
      }),
      status: ['New'],
      safeWord: ['', Validators.required],
      duressWord: [''],
      caseNumber: [''],
      accountNumber: [''],
      harmPersons: [''],
      relatedPersons: this.formBuilder.array([]),
      organizationId: ['', Validators.required],
      advocateId: [null, Validators.required],
      userType: [null],
      devicePhone: [''],
      info: this.formBuilder.group({
        nameOfPerpetrators: ['', Validators.required],
        perpetratorDob: ['', Validators.required],
        modelOfperpetrator: ['', Validators.required],
        perpetratorLicensePlate: ['', Validators.required],
        perpetratorOwns: ['', Validators.required],
        activeRestrainingOrder: ['', Validators.required],
        householdMember: this.formBuilder.group({
          adult: ['0'],
          children: ['0'],
        }),
        typeOfResidence: ['', Validators.required],
        latitude: [''],
        longitude: [''],
        entranceLocation: [[], Validators.required],
        basementAttic: ['', Validators.required],
        firearmAtHome: ['', Validators.required],
      }),
      anyDog: [''],
    });
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const offset = 120; // roughly match sticky header + yOffset used in onNavSelected
    let activeIndex = 0;
    this.enduserSideNav.forEach((nav, i) => {
      const el = document.getElementById(nav.value);
      if (!el) return;
      if (el.getBoundingClientRect().top - offset <= 0) {
        activeIndex = i;
      }
    });
    this.selectedNavIndex = activeIndex;
  }

  checkTypeOfResidence() {
    this.info.get('typeOfResidence')?.valueChanges.subscribe((value) => {
      const lat = this.info.get('latitude');
      const lng = this.info.get('longitude');

      if (value === 'land_plot_point') {
        lat?.setValidators([
          Validators.required,
          Validators.min(-90),
          Validators.max(90),
        ]);

        lng?.setValidators([
          Validators.required,
          Validators.min(-180),
          Validators.max(180),
        ]);
      } else {
        lat?.clearValidators();
        lng?.clearValidators();
        lat?.setValue(null);
        lng?.setValue(null);
      }

      lat?.updateValueAndValidity();
      lng?.updateValueAndValidity();
    });
  }

  getAllOrganizations() {
    this.organizationService
      .getAllOrganizations()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.organizationsList = response.data.rows;
          if (
            this.organizationsList.length === 1 &&
            Object.keys(this.formData).length == 0
          ) {
            this.userForm.patchValue({
              organizationId: this.organizationsList[0]?.id,
            });
            this.getAdvocates(this.userForm.get('organizationId')?.value);
          }
          this.syncUserTypeVisibility();
        },
        error: (e) => {},
      });
  }

  private syncUserTypeVisibility(organizationId?: string | null): void {
    if (this.isLoggedInEndUser) {
      this.showUserType = false;
      return;
    }

    const orgId = organizationId ?? this.userForm.get('organizationId')?.value;
    const org = this.organizationsList.find((o) => o.id === orgId);
    this.showUserType = org?.settings?.threatPairModuleEnabled === true;

    if (!this.showUserType) {
      this.userForm.controls['userType'].setValue(null);
    }
  }


  getAdvocates(organizationId?: string) {
    this.adminService
      .getAdvocates({ organizationId, fromAdd: true })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.advocateList = response.data.rows.map((admin: any) => {
            admin.displayName = this.nightwebUtil.getDisplayName(
              admin?.demographic
            );
            return admin;
          });
          if (this.editEnduser && this.formData?.advocateId) {
            this.ensureAssignedAdvocateInList();
            this.userForm.controls['advocateId'].setValue(
              this.formData.advocateId
            );
            return;
          }
          if (this.advocateList.length === 1) {
            this.userForm.patchValue({
              advocateId: this.advocateList[0]?.id,
            });
            return;
          }
          this.userForm.controls['advocateId'].setValue(null);
        },
        error: (e) => {},
      });
  }

  private ensureAssignedAdvocateInList(): void {
    const advocateId = this.formData?.advocateId;
    if (!advocateId) {
      return;
    }
  
    const alreadyInList = this.advocateList.some(
      (advocate) => advocate.id === advocateId
    );
    if (alreadyInList) {
      return;
    }
  
    // API may return only logged-in advocate; keep end user's assigned advocate visible
    this.advocateList = [
      {
        id: advocateId,
        displayName: this.formData?.advocate?.name ?? 'Assigned Advocate',
      },
      //...this.advocateList,
    ];
  }

  onNavSelected(index: number, elem: string) {
    this.selectedNavIndex = index;
    const yOffset = -100;
    const element = document.querySelector(elem)!;
    const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  validateDateField(controlName: string) {
    const control = this.userForm.get(controlName);
    const isValid = this.dateUtility.isValidPastDate(control?.value);

    if (!isValid) {
      control?.setErrors({ invalidDate: true });
    } else {
      control?.setErrors(null);
    }
  }

  save() {
    this.validateDateField(this.FORM_CONTROL.DEMOGRAPHIC_DOB);
    this.validateDateField(this.FORM_CONTROL.INFO_PERPETRATOR_DOB);
    this.submitted = true;
    const formValue = this.userForm.getRawValue();
    let user = {
      ...formValue,
      info: {
        ...formValue.info,
        entranceLocation: Array.isArray(
          formValue.info?.entranceLocation
        )
          ? formValue.info.entranceLocation.join(',')
          : '',
      },
    };

    if (this.userForm.valid) {
      this.loading = true;

      if (this.userForm.get('id')?.value) {
        delete user['status'];
        if (this.isLoggedInEndUser) {
          this.userService.updateRapidAdd(user)
            .pipe(
              finalize(() => {
                this.loading = false;
              })
            )
            .subscribe({
              next: (response) => {
                if (response.statusCode == this.status_codes.OK) {
                  this.toasterService.show(
                    'Success',
                    MESSAGE_CONSTANTS.USER_UPDATED
                  );
                  this.dataEmitter.emit(response.data);
                  //this.router.navigateByUrl('/end-users/'+this.userForm.get('id')?.value);
                }
              },
              error: (e) => {},
            });
        }else{
          this.userService
            .update(user)
            .pipe(
              finalize(() => {
                this.loading = false;
              })
            )
            .subscribe({
              next: (response) => {
                if (response.statusCode == this.status_codes.OK) {
                  this.dataEmitter.emit(response.data);
                  this.toasterService.show(
                    'Success',
                    MESSAGE_CONSTANTS.USER_UPDATED
                  );
                }
              },
              error: (e) => {},
            });
        }
      } else {
        this.userService
          .create(user)
          .pipe(
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (response) => {
              if (response.statusCode == this.status_codes.OK) {
                this.toasterService.show(
                  'Success',
                  MESSAGE_CONSTANTS.USER_ADDED
                );
                this.dataEmitter.emit(response.data);
              }
            },
            error: (e) => {},
          });
      }
    }
  }

  sendData() {
    const data = false;
    this.dataEmitter.emit(data);
  }

  receiveAutocompleteValue(state: any) {
    this.address.controls['home'].patchValue({ state });
  }

  receiveSelectedValue(event: any) {
    this.userForm.patchValue({
      [event.controlName]: event.value,
    });
    if (event.controlName === 'organizationId') {
      this.getAdvocates(event.value);
      this.syncUserTypeVisibility(event.value);
    }
  }

  changeRadioData(type: string, event: any) {
    if (type == 'perpetratorown') {
      this.info.controls['perpetratorOwns'].setValue(event.target.value);
    } else if (type == 'restraining') {
      this.info.controls['activeRestrainingOrder'].setValue(event.target.value);
    } else if (type == 'firearm') {
      this.info.controls['firearmAtHome'].setValue(event.target.value);
    } else {
      this.userForm.controls['anyDog'].setValue(event.target.value);
    }
  }

  onValueChange(event: Event, type: string): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const value = Number(inputValue);
    if (value < 0) {
      if (type == 'adult') this.householdMember.controls['adult'].setValue(0);
      else this.householdMember.controls['children'].setValue(0);
    }
  }

  onDateSelectChange({
    event,
    controlName,
  }: {
    event: Date;
    controlName: string;
  }) {
    const parsedDate = this.dateUtility.formatDate(
      event,
      NIGHTWEB_APP_CONSTANT.defaultMomentDateFormat
    );
    this.userForm.get(controlName)?.setValue(parsedDate);
  }

  onDateInputChange({ controlName }: { controlName: string }) {
    this.validateDateField(controlName);
  }
}
